import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

type UserRole = 'CLIENT' | 'PROSPECT' | 'ADMIN' | 'ATTORNEY' | 'CASE_MANAGER' | 
  'OFFICE_MANAGER' | 'INQUIRY_MANAGER' | 'LEAD_CASE_MANAGER' | 
  'MEDICAL_RECORDS_COORDINATOR' | 'PARALEGAL';

// Staff roles that are allowed to access the staff portal
const STAFF_ROLES: UserRole[] = [
  'ADMIN', 
  'ATTORNEY', 
  'CASE_MANAGER',
  'OFFICE_MANAGER',
  'INQUIRY_MANAGER',
  'LEAD_CASE_MANAGER',
  'MEDICAL_RECORDS_COORDINATOR',
  'PARALEGAL'
];

// Client roles that should be redirected to client portal
const CLIENT_ROLES: UserRole[] = ['CLIENT', 'PROSPECT'];

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Optional: Restrict access to specific roles. Defaults to all staff roles. */
  allowedRoles?: UserRole[];
  /** Optional: Custom redirect path for unauthorized users */
  redirectTo?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  userRole: UserRole | null;
  isLoading: boolean;
}

/**
 * ProtectedRoute - Guards staff-only routes
 * 
 * Validates:
 * 1. User is authenticated
 * 2. User role is ADMIN, ATTORNEY, or CASE_MANAGER (or specified allowedRoles)
 * 
 * Redirects:
 * - /signin if not authenticated
 * - /client/news if client user attempts access (Requirement 1.4, 1.7)
 * 
 * Requirements: 1.4, 1.7, 1.8
 */
export function ProtectedRoute({ 
  children, 
  allowedRoles = STAFF_ROLES,
  redirectTo 
}: ProtectedRouteProps) {
  const location = useLocation();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    userRole: null,
    isLoading: true,
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      await getCurrentUser();
      
      // Check user role from Cognito session tokens (Requirement 1.8)
      const session = await fetchAuthSession();
      const userRole = session.tokens?.idToken?.payload['custom:role'] as UserRole;

      setAuthState({
        isAuthenticated: true,
        userRole,
        isLoading: false,
      });
    } catch {
      setAuthState({
        isAuthenticated: false,
        userRole: null,
        isLoading: false,
      });
    }
  };

  // Show loading state while checking authentication
  if (authState.isLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Redirect to signin if not authenticated
  if (!authState.isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Redirect client users to client portal instead of signing them out (Requirement 1.4, 1.7)
  if (authState.userRole && CLIENT_ROLES.includes(authState.userRole)) {
    return <Navigate to={redirectTo || '/client/news'} replace />;
  }

  // Check if user has one of the allowed roles
  const isAuthorized = authState.userRole && allowedRoles.includes(authState.userRole);
  
  if (!isAuthorized) {
    // If not authorized but authenticated, redirect to appropriate location
    return <Navigate to={redirectTo || '/signin'} replace />;
  }

  return <>{children}</>;
}
